import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Cog6ToothIcon, 
  CurrencyDollarIcon,
  BellIcon,
  ComputerDesktopIcon,
  KeyIcon,
  EyeIcon,
  EyeSlashIcon,
  CpuChipIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  ClockIcon,
  DocumentChartBarIcon,
  NewspaperIcon,
  SquaresPlusIcon,
  ArrowsPointingOutIcon,
  ArrowPathIcon,
  LanguageIcon,
  PaintBrushIcon,
  SunIcon,
  MoonIcon,
  SparklesIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import Toast from './Toast';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useGlobalState } from '../contexts/GlobalStateContext';
import { getApiUrl, getSocketUrl } from '../utils/api';
import io from 'socket.io-client';

interface SettingsProps {
  highlightSection?: string;
}

interface Settings {
  // 거래소 API 설정
  exchange: string;
  currency: string;
  
  // 바이낸스
  binanceApiKey: string;
  binanceSecretKey: string;
  binanceTestnet: boolean;
  
  // 업비트
  upbitAccessKey: string;
  upbitSecretKey: string;
  
  // 빗썸
  bithumbApiKey: string;
  bithumbSecretKey: string;
  
  // 거래 설정
  defaultTradingPair: string;
  baseTradeAmount: number;
  maxTradeAmount: number;
  stopLossPercentage: number;
  takeProfitPercentage: number;
  
  // 고급 거래 설정
  enableSlippage: boolean;
  slippageTolerance: number;
  enableDCA: boolean;
  dcaIntervals: number;
  dcaAmount: number;
  enableGridTrading: boolean;
  gridLevels: number;
  gridSpacing: number;
  
  // 리스크 관리
  maxDrawdown: number;
  dailyLossLimit: number;
  positionSizing: string;
  diversificationLevel: number;
  
  // 봇 설정
  botEnabled: boolean;
  tradingInterval: number;
  maxDailyTrades: number;
  riskLevel: string;
  aiTimerInterval: number; // AI 봇 타이머 간격 (초)
  
  // 통합 AI 설정 - 개선된 구조
  aiMode: 'single' | 'consensus'; // 단일 AI vs AI 컨센서스
  primaryAiProvider: string; // 주 AI 제공자
  primaryAiModel: string; // 주 AI 모델 상세 설정
  
  // AI 컨센서스 설정
  consensusEnabled: boolean;
  consensusProviders: string[]; // 참여할 AI 제공자들
  consensusModels: { [key: string]: string }; // 각 제공자별 모델
  consensusWeighting: { [key: string]: number }; // 각 AI별 가중치
  
  // 용도별 AI 설정
  chatbotAiProvider: string;
  chatbotAiModel: string;
  analysisAiProvider: string;
  analysisAiModel: string;
  
  // AI API 키들
  openaiApiKey: string;
  anthropicApiKey: string;
  googleApiKey: string;
  xaiApiKey: string;
  deepseekApiKey: string;
  metaApiKey: string;
  perplexityApiKey: string;
  cohereApiKey: string;
  ollamaServerUrl: string;
  
  // AI 모델별 세부 설정
  aiProviderSettings: {
    openai: {
      enabled: boolean;
      apiKey: string;
      model: string;
      temperature: number;
      maxTokens: number;
    };
    anthropic: {
      enabled: boolean;
      apiKey: string;
      model: string;
      temperature: number;
      maxTokens: number;
    };
    google: {
      enabled: boolean;
      apiKey: string;
      model: string;
      temperature: number;
      maxTokens: number;
    };
    xai: {
      enabled: boolean;
      apiKey: string;
      model: string;
      temperature: number;
      maxTokens: number;
    };
    deepseek: {
      enabled: boolean;
      apiKey: string;
      model: string;
      temperature: number;
      maxTokens: number;
    };
    ollama: {
      enabled: boolean;
      serverUrl: string;
      model: string;
      temperature: number;
      maxTokens: number;
    };
  };
  
  // 시스템 프롬프트
  systemPrompt: string;
  
  // 레거시 호환성을 위한 필드들
  aiModel: string;
  chatgptModel: string;
  
  // 데이터 소스 설정
  primaryDataSource: string;
  backupDataSource: string;
  dataUpdateInterval: number;
  enableRealTimeData: boolean;
  newsApiKey: string; // News API 키 추가
  
  // 백테스트 설정
  backtestPeriod: string;
  backtestStartDate: string;
  backtestEndDate: string;
  backtestInitialCapital: number;
  backtestCommission: number;
  
  // 텔레그램 알림 설정
  telegramEnabled: boolean;
  telegramBotToken: string;
  telegramChatId: string;
  telegramNotifications: {
    tradeExecution: boolean;
    aiAnalysis: boolean;
    priceAlerts: boolean;
    profitLoss: boolean;
    errorMessages: boolean;
    dailyReport: boolean;
    weeklyReport: boolean;
  };
  
  // 기존 알림 설정
  tradeNotifications: boolean;
  errorNotifications: boolean;
  priceAlerts: boolean;
  
  // 로그 및 모니터링
  enableLogging: boolean;
  logLevel: string;
  logRetentionDays: number;
  enablePerformanceMonitoring: boolean;
  
  // 시스템 설정
  language: string;
  theme: string;
  autoSave: boolean;
  autoBackup: boolean;
  backupInterval: number;

  consensusAi1Provider: string;
  consensusAi1Model: string;
  consensusAi2Provider: string;
  consensusAi2Model: string;
  consensusAi3Provider: string;
  consensusAi3Model: string;

  // 컨센서스 시뮬레이션 설정

  // 뉴스 수집 설정
  newsUpdateInterval: number; // 뉴스 업데이트 주기 (분)
  newsMaxArticles: number; // 최대 뉴스 개수
  enableRssNews: boolean; // RSS 뉴스 수집 활성화
  enableApiNews: boolean; // API 뉴스 수집 활성화
  
  // 뉴스 API 키들
  cryptoPanicApiKey: string;
  messariApiKey: string;
  cryptoCompareApiKey: string;

  // UI 레이아웃 설정 추가
  uiLayout: {
    dashboard: {
      cryptoDashboard: { x: number; y: number; w: number; h: number; visible: boolean };
      botTimer: { x: number; y: number; w: number; h: number; visible: boolean };
      botControl: { x: number; y: number; w: number; h: number; visible: boolean };
      cryptoNews: { x: number; y: number; w: number; h: number; visible: boolean };
      performance: { x: number; y: number; w: number; h: number; visible: boolean };
      trades: { x: number; y: number; w: number; h: number; visible: boolean };
    };
    gridSize: number;
    snapToGrid: boolean;
  };
}

const SettingsComponent: React.FC<SettingsProps> = ({ highlightSection }) => {
  const { theme } = useTheme();
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('exchange');
  const [showApiKeys, setShowApiKeys] = useState(false);
  const [isHighlighted, setIsHighlighted] = useState(false);
  const consensusRef = useRef<HTMLDivElement>(null);
  const apiKeysRef = useRef<HTMLDivElement>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showTelegramSettings, setShowTelegramSettings] = useState(false);
  const [versionInfo, setVersionInfo] = useState<any>(null);
  
  // 토스트 상태
  const [toast, setToast] = useState({
    isVisible: false,
    message: '',
    type: 'success' as 'success' | 'error'
  });

  // highlightSection이 변경될 때 처리
  useEffect(() => {
    if (highlightSection === 'ai-consensus') {
      setActiveTab('ai-unified');
      setIsHighlighted(true);
      
      // 약간의 지연 후 스크롤 및 하이라이트
      setTimeout(() => {
        if (consensusRef.current) {
          consensusRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }
      }, 300);

      // 3초 후 하이라이트 제거
      setTimeout(() => {
        setIsHighlighted(false);
      }, 3000);
    } else if (highlightSection === 'api-keys') {
      setActiveTab('ai-unified');
      setIsHighlighted(true);
      
      // 약간의 지연 후 스크롤 및 하이라이트
      setTimeout(() => {
        if (apiKeysRef.current) {
          apiKeysRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }
      }, 300);

      // 3초 후 하이라이트 제거
      setTimeout(() => {
        setIsHighlighted(false);
      }, 3000);
    }
  }, [highlightSection]);

  // 설정 상태
  const [settings, setSettings] = useState<Settings>({
    // 거래소 API
    exchange: 'binance',
    currency: 'USD',
    
    // 바이낸스
    binanceApiKey: '',
    binanceSecretKey: '',
    binanceTestnet: true,
    
    // 업비트
    upbitAccessKey: '',
    upbitSecretKey: '',
    
    // 빗썸
    bithumbApiKey: '',
    bithumbSecretKey: '',
    
    // 거래 설정
    defaultTradingPair: 'BTCUSDT',
    baseTradeAmount: 100,
    maxTradeAmount: 1000,
    stopLossPercentage: 5,
    takeProfitPercentage: 10,
    
    // 고급 거래 설정
    enableSlippage: true,
    slippageTolerance: 0.5,
    enableDCA: false,
    dcaIntervals: 24,
    dcaAmount: 50,
    enableGridTrading: false,
    gridLevels: 10,
    gridSpacing: 2.0,
    
    // 리스크 관리
    maxDrawdown: 20,
    dailyLossLimit: 5,
    positionSizing: 'fixed',
    diversificationLevel: 3,
    
    // 봇 설정
    botEnabled: false,
    tradingInterval: 60,
    maxDailyTrades: 10,
    riskLevel: 'medium',
    aiTimerInterval: 900, // AI 봇 타이머 간격 (초) - 15분
    
    // 통합 AI 설정 - 개선된 구조
    aiMode: 'single',
    primaryAiProvider: 'rule-based',
    primaryAiModel: 'rule-based',
    
    // AI 컨센서스 설정
    consensusEnabled: false,
    consensusProviders: [],
    consensusModels: {},
    consensusWeighting: {},
    
    // 용도별 AI 설정
    chatbotAiProvider: 'rule-based',
    chatbotAiModel: 'rule-based',
    analysisAiProvider: 'rule-based',
    analysisAiModel: 'rule-based',
    
    // AI API 키들
    openaiApiKey: '',
    anthropicApiKey: '',
    googleApiKey: '',
    xaiApiKey: '',
    deepseekApiKey: '',
    metaApiKey: '',
    perplexityApiKey: '',
    cohereApiKey: '',
    ollamaServerUrl: '',
    
    // AI 모델별 세부 설정
    aiProviderSettings: {
      openai: {
        enabled: false,
        apiKey: '',
        model: '',
        temperature: 0,
        maxTokens: 0
      },
      anthropic: {
        enabled: false,
        apiKey: '',
        model: '',
        temperature: 0,
        maxTokens: 0
      },
      google: {
        enabled: false,
        apiKey: '',
        model: '',
        temperature: 0,
        maxTokens: 0
      },
      xai: {
        enabled: false,
        apiKey: '',
        model: '',
        temperature: 0,
        maxTokens: 0
      },
      deepseek: {
        enabled: false,
        apiKey: '',
        model: '',
        temperature: 0,
        maxTokens: 0
      },
      ollama: {
        enabled: false,
        serverUrl: '',
        model: '',
        temperature: 0,
        maxTokens: 0
      }
    },
    
    // 시스템 프롬프트
    systemPrompt: '당신은 암호화폐 거래 전문가 AI 어시스턴트입니다. 현재 비트코인 가격과 시장 데이터를 활용하여 도움이 되는 투자 조언과 분석을 제공해주세요. 한국어로 답변해주세요.',
    
    // 레거시 호환성을 위한 필드들
    aiModel: 'rule-based',
    chatgptModel: 'gpt-4o-mini',
    
    // 데이터 소스 설정
    primaryDataSource: 'binance',
    backupDataSource: 'coinmarketcap',
    dataUpdateInterval: 5,
    enableRealTimeData: true,
    newsApiKey: '', // News API 키 추가
    
    // 백테스트 설정
    backtestPeriod: '1year',
    backtestStartDate: '2024-01-01',
    backtestEndDate: '2024-12-31',
    backtestInitialCapital: 10000,
    backtestCommission: 0.1,
    
    // 텔레그램 알림 설정
    telegramEnabled: false,
    telegramBotToken: '',
    telegramChatId: '',
    telegramNotifications: {
      tradeExecution: true,
      aiAnalysis: true,
      priceAlerts: true,
      profitLoss: true,
      errorMessages: true,
      dailyReport: true,
      weeklyReport: false,
    },
    
    // 기존 알림 설정
    tradeNotifications: true,
    errorNotifications: true,
    priceAlerts: true,
    
    // 로그 및 모니터링
    enableLogging: true,
    logLevel: 'info',
    logRetentionDays: 30,
    enablePerformanceMonitoring: true,
    
    // 시스템
    language: 'ko',
    theme: 'dark',
    autoSave: true,
    autoBackup: true,
    backupInterval: 24,

    consensusAi1Provider: 'openai',
    consensusAi1Model: 'gpt-4.1',
    consensusAi2Provider: 'anthropic',
    consensusAi2Model: 'claude-opus-4-20250514',
    consensusAi3Provider: 'google',
    consensusAi3Model: 'gemini-2.5-pro-preview-05-06',

    // 컨센서스 시뮬레이션 설정

    // 뉴스 수집 설정
    newsUpdateInterval: 15, // 뉴스 업데이트 주기 (분)
    newsMaxArticles: 10, // 최대 뉴스 개수
    enableRssNews: true, // RSS 뉴스 수집 활성화
    enableApiNews: true, // API 뉴스 수집 활성화
    
    // 뉴스 API 키들
    cryptoPanicApiKey: '',
    messariApiKey: '',
    cryptoCompareApiKey: '',

    // UI 레이아웃 설정 추가
    uiLayout: {
      dashboard: {
        cryptoDashboard: { x: 0, y: 0, w: 12, h: 6, visible: true },
        botTimer: { x: 0, y: 6, w: 6, h: 3, visible: true },
        botControl: { x: 6, y: 6, w: 6, h: 3, visible: true },
        cryptoNews: { x: 0, y: 9, w: 12, h: 3, visible: true },
        performance: { x: 0, y: 12, w: 12, h: 3, visible: true },
        trades: { x: 0, y: 15, w: 12, h: 6, visible: true },
      },
      gridSize: 12,
      snapToGrid: true,
    },
  });

  // 토스트 표시 함수
  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({
      isVisible: true,
      message,
      type
    });
  };

  // 토스트 닫기 함수
  const closeToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  // 설정 불러오기
  useEffect(() => {
    fetchSettings();

    // WebSocket을 통한 실시간 설정 동기화
    let socket: any = null;
    
    try {
      const serverUrl = getSocketUrl();
      
      socket = io(serverUrl, {
        transports: ['websocket', 'polling'],
        timeout: 10000
      });

      // 설정 업데이트 수신
      socket.on('settingsUpdate', (serverSettings: any) => {
        console.log('⚙️ 설정 페이지에서 설정 업데이트 수신:', serverSettings);
        setSettings(prev => ({ ...prev, ...serverSettings }));
        showToast('다른 클라이언트에서 설정이 변경되어 동기화되었습니다.', 'success');
      });

      // 초기 데이터에서 설정 받기
      socket.on('initialData', (data: any) => {
        if (data.settings) {
          console.log('⚙️ 설정 페이지에서 초기 설정 수신:', data.settings);
          setSettings(prev => ({ ...prev, ...data.settings }));
        }
      });

    } catch (error) {
      console.error('설정 페이지 WebSocket 연결 오류:', error);
    }

    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/api/settings`);
      if (response.ok) {
        const serverSettings = await response.json();
        setSettings(prev => ({ ...prev, ...serverSettings }));
      }
    } catch (error) {
      console.error('설정 불러오기 실패:', error);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${getApiUrl()}/api/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings),
      });

      if (response.ok) {
        showToast('설정이 성공적으로 저장되었습니다.', 'success');
        await fetchSettings();
      } else {
        const error = await response.json();
        showToast(error.message || '설정 저장에 실패했습니다.', 'error');
      }
    } catch (error) {
      console.error('설정 저장 실패:', error);
      showToast('설정 저장 중 오류가 발생했습니다.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key: keyof Settings, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  // Ollama URL 유효성 검사 함수
  const validateOllamaUrl = (url: string): { isValid: boolean; message: string } => {
    if (!url) {
      return { isValid: false, message: 'URL을 입력해주세요' };
    }
    
    try {
      const parsedUrl = new URL(url);
      
      // 프로토콜 검사
      if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
        return { isValid: false, message: 'http:// 또는 https://로 시작해야 합니다' };
      }
      
      // 호스트명 검사
      if (!parsedUrl.hostname) {
        return { isValid: false, message: '올바른 서버 주소를 입력해주세요' };
      }
      
      // 포트 검사 (선택적)
      if (parsedUrl.port && (parseInt(parsedUrl.port) < 1 || parseInt(parsedUrl.port) > 65535)) {
        return { isValid: false, message: '포트 번호는 1-65535 범위여야 합니다' };
      }
      
      return { isValid: true, message: '올바른 형식입니다' };
    } catch (error) {
      return { isValid: false, message: '올바르지 않은 URL 형식입니다' };
    }
  };

  // ChatGPT 모델 목록

  // AI 제공자별 사용 가능한 모델 반환
  const getAvailableModels = (provider: string) => {
    switch (provider) {
      case 'openai':
        return [
          { value: 'gpt-4.1', label: 'GPT-4.1 (2025 최신, 코딩 특화)' },
          { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini (빠르고 경제적)' },
          { value: 'gpt-4.1-nano', label: 'GPT-4.1 Nano (초고속, API 전용)' },
          { value: 'gpt-4.5-preview', label: 'GPT-4.5 Preview (Orion, 가장 강력함)' },
          { value: 'o4-mini', label: 'o4-mini (2025 추론 모델)' },
          { value: 'o3', label: 'o3 (고급 추론)' },
          { value: 'o3-mini', label: 'o3-mini (추론 특화)' },
          { value: 'o1-preview', label: 'o1-preview (고급 추론)' },
          { value: 'o1-mini', label: 'o1-mini (빠른 추론)' },
          { value: 'chatgpt-4o-latest', label: 'ChatGPT-4o Latest (최신 버전)' },
          { value: 'gpt-4o', label: 'GPT-4o (멀티모달)' },
          { value: 'gpt-4o-mini', label: 'GPT-4o mini (경제적)' },
          { value: 'gpt-4-turbo', label: 'GPT-4 Turbo (고성능)' },
          { value: 'gpt-4', label: 'GPT-4 (표준)' },
          { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo (기본)' }
        ];
      case 'anthropic':
        return [
          { value: 'claude-opus-4-20250514', label: 'Claude Opus 4 (2025 최신, 최고성능)' },
          { value: 'claude-sonnet-4-20250514', label: 'Claude Sonnet 4 (2025 최신, 균형)' },
          { value: 'claude-3-7-sonnet-20250219', label: 'Claude 3.7 Sonnet (하이브리드 추론)' },
          { value: 'claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet v2 (개선된 버전)' },
          { value: 'claude-3-5-sonnet-20240620', label: 'Claude 3.5 Sonnet (초기 버전)' },
          { value: 'claude-3-5-haiku-20241022', label: 'Claude 3.5 Haiku (빠름)' },
          { value: 'claude-3-opus-20240229', label: 'Claude 3 Opus (고성능)' },
          { value: 'claude-3-sonnet-20240229', label: 'Claude 3 Sonnet (균형)' },
          { value: 'claude-3-haiku-20240307', label: 'Claude 3 Haiku (경제적)' }
        ];
      case 'google':
        return [
          { value: 'gemini-2.5-pro-preview-05-06', label: 'Gemini 2.5 Pro Preview (2025 최신, 추론 특화)' },
          { value: 'gemini-2.5-flash-preview-05-20', label: 'Gemini 2.5 Flash Preview (2025 최신, 빠름)' },
          { value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash (차세대 기능)' },
          { value: 'gemini-2.0-flash-lite', label: 'Gemini 2.0 Flash-Lite (경제적)' },
          { value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro (2M 토큰 컨텍스트)' },
          { value: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash (다목적)' },
          { value: 'gemini-1.5-flash-8b', label: 'Gemini 1.5 Flash-8B (경량)' },
          { value: 'gemini-pro', label: 'Gemini Pro (표준)' },
          { value: 'gemini-pro-vision', label: 'Gemini Pro Vision (멀티모달)' }
        ];
      case 'xai':
        return [
          { value: 'grok-3', label: 'Grok 3 (2025 최신)' },
          { value: 'grok-3-fast', label: 'Grok 3 Fast (빠른 버전)' },
          { value: 'grok-3-mini', label: 'Grok 3 Mini (경량)' },
          { value: 'grok-vision-2', label: 'Grok Vision 2 (비전 특화)' },
          { value: 'grok-2', label: 'Grok 2 (표준)' },
          { value: 'grok-beta', label: 'Grok Beta (실험적)' }
        ];
      case 'deepseek':
        return [
          { value: 'deepseek-r1', label: 'DeepSeek R1 (2025 추론 모델)' },
          { value: 'deepseek-v3', label: 'DeepSeek V3 (2025 최신)' },
          { value: 'deepseek-prover-v2', label: 'DeepSeek Prover V2 (수학 특화)' },
          { value: 'deepseek-chat', label: 'DeepSeek Chat (대화형)' },
          { value: 'deepseek-coder', label: 'DeepSeek Coder (코딩 특화)' }
        ];
      case 'ollama':
        return [
          { value: 'gemma2:27b', label: 'Gemma 2 27B (고성능 모델)' },
          { value: 'gemma2:9b', label: 'Gemma 2 9B (균형잡힌 성능)' },
          { value: 'gemma2:2b', label: 'Gemma 2 2B (경량 모델)' },
          { value: 'llama3.2', label: 'Llama 3.2 (Meta 최신)' },
          { value: 'llama3.1', label: 'Llama 3.1 (안정적)' },
          { value: 'llama3', label: 'Llama 3 (표준)' },
          { value: 'mistral', label: 'Mistral 7B (효율적)' },
          { value: 'mixtral', label: 'Mixtral 8x7B (전문가 모델)' },
          { value: 'codellama', label: 'CodeLlama (코딩 특화)' },
          { value: 'dolphin-mixtral', label: 'Dolphin Mixtral (대화 특화)' },
          { value: 'qwen2.5', label: 'Qwen 2.5 (중국어 강화)' },
          { value: 'deepseek-coder', label: 'DeepSeek Coder (코딩 전문)' }
        ];
      default:
        return [{ value: '', label: '모델 선택' }];
    }
  };

  // AI 제공자 정보 반환
  const getProviderInfo = (provider: string) => {
    switch (provider) {
      case 'openai':
        return {
          name: '🔥 OpenAI (ChatGPT)',
          description: '범용 AI, 뛰어난 텍스트 생성 및 분석 능력'
        };
      case 'anthropic':
        return {
          name: '🧠 Anthropic (Claude)',
          description: '안전하고 신뢰할 수 있는 AI, 논리적 사고 우수'
        };
      case 'google':
        return {
          name: '✨ Google (Gemini)',
          description: '구글의 최신 AI, 멀티모달 기능 지원'
        };
      case 'xai':
        return {
          name: '🚀 xAI (Grok)',
          description: '일론 머스크의 AI, 실시간 정보 접근 가능'
        };
      case 'deepseek':
        return {
          name: '🎯 DeepSeek',
          description: '중국의 강력한 AI, 코딩 및 수학 특화'
        };
      case 'ollama':
        return {
          name: '🏠 Ollama (로컬 AI)',
          description: '로컬에서 실행되는 오픈소스 AI, 개인정보 보호 우수'
        };
      default:
        return {
          name: '알 수 없음',
          description: '정보가 없습니다'
        };
    }
  };

  // 거래소별 거래 쌍
  const getTradingPairs = () => {
    switch (settings.exchange) {
      case 'binance':
        return ['BTCUSDT', 'ETHUSDT', 'BNBUSDT', 'ADAUSDT', 'XRPUSDT', 'SOLUSDT', 'DOTUSDT', 'DOGEUSDT', 'AVAXUSDT', 'LINKUSDT'];
      case 'upbit':
        return ['KRW-BTC', 'KRW-ETH', 'KRW-XRP', 'KRW-ADA', 'KRW-DOT', 'KRW-LINK', 'KRW-BCH', 'KRW-LTC', 'KRW-EOS', 'KRW-TRX'];
      case 'bithumb':
        return ['BTC_KRW', 'ETH_KRW', 'XRP_KRW', 'LTC_KRW', 'BCH_KRW', 'EOS_KRW', 'ADA_KRW', 'DOT_KRW', 'LINK_KRW', 'TRX_KRW'];
      default:
        return ['BTCUSDT'];
    }
  };

  // 새로운 탭 구조 정의 - 깔끔하고 일관된 구조
  const tabs = [
    {
      id: 'exchange',
      label: '거래소 연결',
      icon: CurrencyDollarIcon,
      description: '거래소 API 키 설정'
    },
    {
      id: 'trading',
      label: '거래 설정',
      icon: ChartBarIcon,
      description: '기본 거래 전략 설정'
    },
    {
      id: 'ai',
      label: 'AI 설정',
      icon: CpuChipIcon,
      description: 'AI 모델 및 API 키 설정'
    },
    {
      id: 'notifications',
      label: '알림 설정',
      icon: BellIcon,
      description: '텔레그램 및 브라우저 알림'
    },
    {
      id: 'system',
      label: '시스템',
      icon: Cog6ToothIcon,
      description: '시스템 및 로그 설정'
    },
    {
      id: 'backtest',
      label: '백테스트',
      icon: ClockIcon,
      description: '과거 데이터를 활용한 전략 검증'
    }
  ];

  // 예쁜 API 키 마스킹 함수
  const maskApiKey = (key: string): string => {
    if (!key || key.length === 0) return '';
    if (key.length <= 8) {
      return '●'.repeat(key.length);
    }
    const start = key.substring(0, 4);
    const end = key.substring(key.length - 4);
    const middle = '●'.repeat(Math.min(key.length - 8, 12));
    return `${start}${middle}${end}`;
  };

  // 키 상태 표시
  const getKeyStatus = (key: string) => {
    if (!key) return { 
      status: 'empty', 
      icon: '🔑', 
      color: theme === 'dark' ? '#9ca3af' : '#6b7280',
      bgColor: theme === 'dark' ? 'rgba(107, 114, 128, 0.1)' : 'rgba(156, 163, 175, 0.1)'
    };
    if (key.length < 20) return { 
      status: 'weak', 
      icon: '⚠️', 
      color: theme === 'dark' ? '#fbbf24' : '#d97706',
      bgColor: theme === 'dark' ? 'rgba(251, 191, 36, 0.1)' : 'rgba(217, 119, 6, 0.1)'
    };
    return { 
      status: 'valid', 
      icon: '✅', 
      color: theme === 'dark' ? '#34d399' : '#059669',
      bgColor: theme === 'dark' ? 'rgba(52, 211, 153, 0.1)' : 'rgba(5, 150, 105, 0.1)'
    };
  };

  // 텔레그램 연결 테스트
  const testTelegramConnection = async () => {
    if (!settings.telegramBotToken || !settings.telegramChatId) {
      showToast('봇 토큰과 채팅 ID를 먼저 입력해주세요.', 'error');
      return;
    }

    try {
      const response = await fetch(`${getApiUrl()}/api/telegram/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          botToken: settings.telegramBotToken,
          chatId: settings.telegramChatId,
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        showToast('텔레그램 연결 테스트 성공!', 'success');
      } else {
        showToast(result.message || '연결 테스트 실패', 'error');
      }
    } catch (error) {
      console.error('연결 테스트 오류:', error);
      showToast('연결 테스트 중 오류가 발생했습니다.', 'error');
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'exchange':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold" style={getLabelStyle()}>거래소 API 설정</h3>
              <div className="text-xs" style={getDescriptionStyle()}>
                1단계: 거래를 위한 필수 설정
              </div>
            </div>
            
            {/* 거래소 선택 */}
            <div>
              <label className="block text-sm font-medium mb-2" style={getLabelStyle()}>
                거래소 선택
              </label>
              <select
                value={settings.exchange}
                onChange={(e) => updateSetting('exchange', e.target.value)}
                style={getInputStyle()}
                className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="binance">바이낸스 (Binance) - 글로벌 최대 거래소</option>
                <option value="upbit">업비트 (Upbit) - 한국 원화 거래</option>
                <option value="bithumb">빗썸 (Bithumb) - 한국 원화 거래</option>
              </select>
            </div>

            {/* 화폐 단위 선택 */}
            <div>
              <label className="block text-sm font-medium mb-2" style={getLabelStyle()}>
                화폐 단위
              </label>
              <select
                value={settings.currency}
                onChange={(e) => updateSetting('currency', e.target.value)}
                style={getInputStyle()}
                className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="USD">💵 USD (달러) - 글로벌 기준</option>
                <option value="KRW">🇰🇷 KRW (원화) - 한국 기준</option>
              </select>
              <p className="text-xs mt-1" style={getDescriptionStyle()}>
                잔고 및 가격 표시에 사용할 화폐 단위를 선택하세요
              </p>
            </div>

            {/* 바이낸스 API 설정 */}
            {settings.exchange === 'binance' && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2" style={getLabelStyle()}>
                    바이낸스 API 키
                  </label>
                  <div className="relative">
                    <input
                      type={showApiKeys ? "text" : "password"}
                      value={settings.binanceApiKey}
                      onChange={(e) => updateSetting('binanceApiKey', e.target.value)}
                      style={{ 
                        ...getInputStyle(),
                        letterSpacing: showApiKeys ? 'normal' : '0.15em'
                      }}
                      className="w-full border rounded-lg px-4 py-2 pr-12 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                      placeholder="바이낸스에서 발급받은 API 키를 입력하세요"
                      autoComplete="off"
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiKeys(!showApiKeys)}
                      style={{ 
                        color: theme === 'dark' ? '#9ca3af' : '#6b7280'
                      }}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 hover:opacity-75 transition-colors"
                    >
                      {showApiKeys ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                    </button>
                  </div>
                  {settings.binanceApiKey && (
                    <div 
                      className="mt-2 p-2 rounded-lg border"
                      style={{ 
                        backgroundColor: getKeyStatus(settings.binanceApiKey).bgColor,
                        borderColor: getKeyStatus(settings.binanceApiKey).color
                      }}
                    >
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center space-x-2">
                          <span>{getKeyStatus(settings.binanceApiKey).icon}</span>
                          <span style={{ color: getKeyStatus(settings.binanceApiKey).color }}>
                            저장된 키: <span className="font-mono">{maskApiKey(settings.binanceApiKey)}</span>
                          </span>
                        </div>
                        <span style={getDescriptionStyle()}>{settings.binanceApiKey.length}자</span>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={getLabelStyle()}>
                    바이낸스 시크릿 키
                  </label>
                  <div className="relative">
                    <input
                      type={showApiKeys ? "text" : "password"}
                      value={settings.binanceSecretKey}
                      onChange={(e) => updateSetting('binanceSecretKey', e.target.value)}
                      style={{ 
                        ...getInputStyle(),
                        letterSpacing: showApiKeys ? 'normal' : '0.15em'
                      }}
                      className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                      placeholder="바이낸스에서 발급받은 시크릿 키를 입력하세요"
                      autoComplete="off"
                    />
                  </div>
                  {settings.binanceSecretKey && (
                    <div 
                      className="mt-2 p-2 rounded-lg border"
                      style={{ 
                        backgroundColor: getKeyStatus(settings.binanceSecretKey).bgColor,
                        borderColor: getKeyStatus(settings.binanceSecretKey).color
                      }}
                    >
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center space-x-2">
                          <span>{getKeyStatus(settings.binanceSecretKey).icon}</span>
                          <span style={{ color: getKeyStatus(settings.binanceSecretKey).color }}>
                            저장된 시크릿: <span className="font-mono">{maskApiKey(settings.binanceSecretKey)}</span>
                          </span>
                        </div>
                        <span style={getDescriptionStyle()}>{settings.binanceSecretKey.length}자</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="testnet"
                    checked={settings.binanceTestnet}
                    onChange={(e) => updateSetting('binanceTestnet', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="testnet" className="ml-2 text-sm" style={getLabelStyle()}>
                    테스트넷 사용 (실제 거래하지 않음)
                  </label>
                </div>
                
                <div className="p-4 rounded-lg" style={{
                  backgroundColor: theme === 'dark' ? 'rgba(234, 179, 8, 0.1)' : 'rgba(234, 179, 8, 0.05)',
                  borderLeft: `4px solid ${theme === 'dark' ? 'rgba(234, 179, 8, 0.5)' : 'rgba(234, 179, 8, 0.8)'}`
                }}>
                  <h4 className="flex items-center text-sm font-medium mb-2" style={{
                    color: theme === 'dark' ? 'rgb(251, 191, 36)' : 'rgb(161, 98, 7)'
                  }}>
                    ⚠️ 바이낸스 API 설정 가이드
                  </h4>
                  <ul className="space-y-1 text-xs" style={{
                    color: theme === 'dark' ? 'rgb(209, 213, 219)' : 'rgb(55, 65, 81)'
                  }}>
                    <li>• API 키 생성 시 "현물 거래" 권한을 활성화하세요</li>
                    <li>"출금" 권한은 절대 활성화하지 마세요</li>
                    <li>• IP 제한을 설정하여 보안을 강화하세요</li>
                    <li>• API 키를 다른 곳에 절대 공유하지 마세요</li>
                  </ul>
                </div>
              </>
            )}
          </div>
        );

      case 'trading':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold" style={getLabelStyle()}>거래 설정</h3>
              <div className="text-xs" style={getDescriptionStyle()}>
                2단계: 기본 거래 설정
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2" style={getLabelStyle()}>
                기본 거래 쌍
              </label>
              <select
                value={settings.defaultTradingPair}
                onChange={(e) => updateSetting('defaultTradingPair', e.target.value)}
                style={getInputStyle()}
                className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {getTradingPairs().map(pair => (
                  <option key={pair} value={pair}>{pair}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={getLabelStyle()}>
                  기본 거래 금액 (USDT)
                </label>
                <input
                  type="number"
                  value={settings.baseTradeAmount}
                  onChange={(e) => updateSetting('baseTradeAmount', Number(e.target.value))}
                  style={getInputStyle()}
                  className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="10"
                  max="10000"
                />
                <p className="text-xs mt-1" style={getDescriptionStyle()}>한 번에 거래할 기본 금액</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={getLabelStyle()}>
                  최대 거래 금액 (USDT)
                </label>
                <input
                  type="number"
                  value={settings.maxTradeAmount}
                  onChange={(e) => updateSetting('maxTradeAmount', Number(e.target.value))}
                  style={getInputStyle()}
                  className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="100"
                  max="100000"
                />
                <p className="text-xs mt-1" style={getDescriptionStyle()}>한 번에 거래할 최대 금액</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={getLabelStyle()}>
                  손절가 (%)
                </label>
                <input
                  type="number"
                  value={settings.stopLossPercentage}
                  onChange={(e) => updateSetting('stopLossPercentage', Number(e.target.value))}
                  style={getInputStyle()}
                  className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1"
                  max="20"
                  step="0.5"
                />
                <p className="text-xs mt-1" style={getDescriptionStyle()}>자동 손절 퍼센트</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={getLabelStyle()}>
                  익절가 (%)
                </label>
                <input
                  type="number"
                  value={settings.takeProfitPercentage}
                  onChange={(e) => updateSetting('takeProfitPercentage', Number(e.target.value))}
                  style={getInputStyle()}
                  className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1"
                  max="50"
                  step="0.5"
                />
                <p className="text-xs mt-1" style={getDescriptionStyle()}>자동 익절 퍼센트</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={getLabelStyle()}>
                  거래 간격 (초)
                </label>
                <input
                  type="number"
                  value={settings.tradingInterval}
                  onChange={(e) => updateSetting('tradingInterval', Number(e.target.value))}
                  style={getInputStyle()}
                  className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="10"
                  max="3600"
                />
                <p className="text-xs mt-1" style={getDescriptionStyle()}>봇 실행 간격</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={getLabelStyle()}>
                  🤖 AI 분석 간격 (초)
                </label>
                <select
                  value={settings.aiTimerInterval}
                  onChange={(e) => updateSetting('aiTimerInterval', Number(e.target.value))}
                  style={getInputStyle()}
                  className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="300">5분 (300초)</option>
                  <option value="600">10분 (600초)</option>
                  <option value="900">15분 (900초) - 권장</option>
                  <option value="1200">20분 (1,200초)</option>
                  <option value="1800">30분 (1,800초)</option>
                  <option value="3600">1시간 (3,600초)</option>
                </select>
                <p className="text-xs mt-1" style={getDescriptionStyle()}>AI 시장 분석 실행 주기</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={getLabelStyle()}>
                  최대 일일 거래 횟수
                </label>
                <input
                  type="number"
                  value={settings.maxDailyTrades}
                  onChange={(e) => updateSetting('maxDailyTrades', Number(e.target.value))}
                  style={getInputStyle()}
                  className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1"
                  max="50"
                />
                <p className="text-xs mt-1" style={getDescriptionStyle()}>하루 최대 거래 제한</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={getLabelStyle()}>
                  리스크 레벨
                </label>
                <select
                  value={settings.riskLevel}
                  onChange={(e) => updateSetting('riskLevel', e.target.value)}
                  style={getInputStyle()}
                  className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="low">낮음 (Low)</option>
                  <option value="medium">보통 (Medium)</option>
                  <option value="high">높음 (High)</option>
                </select>
                <p className="text-xs mt-1" style={getDescriptionStyle()}>거래 위험도 설정</p>
              </div>
            </div>

            <div className="flex items-center p-4 rounded-lg" style={getWidgetStyle()}>
              <input
                type="checkbox"
                id="botEnabled"
                checked={settings.botEnabled}
                onChange={(e) => updateSetting('botEnabled', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="botEnabled" className="ml-2 text-sm" style={getLabelStyle()}>
                자동 거래 활성화
              </label>
            </div>
          </div>
        );

      case 'ai':
        return (
          <div className="space-y-8">
            {/* AI 설정 헤더 */}
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold" style={getLabelStyle()}>🤖 AI 설정</h3>
              <div className="text-sm" style={getDescriptionStyle()}>
                AI 모델 및 서버 설정
              </div>
            </div>

            {/* AI 모드 선택 */}


            {/* AI 제공자 설정 */}
            <div className="p-6 rounded-lg border" style={getWidgetStyle()}>
              <h4 className="text-lg font-semibold mb-4 flex items-center" style={getLabelStyle()}>
                <KeyIcon className="h-5 w-5 mr-2 text-green-500" />
                AI 제공자 및 API 키
              </h4>
              
              <div className="space-y-6">
                {/* OpenAI */}
                <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="font-medium flex items-center" style={getLabelStyle()}>
                      🚀 OpenAI (ChatGPT)
                    </h5>
                    <div className="text-xs" style={getDescriptionStyle()}>
                      가장 인기있는 AI 모델
                    </div>
                  </div>
                  <div className="relative">
                    <input
                      type={showApiKeys ? "text" : "password"}
                      value={settings.openaiApiKey}
                      onChange={(e) => updateSetting('openaiApiKey', e.target.value)}
                      style={getInputStyle()}
                      className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                      placeholder="sk-proj-..."
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiKeys(!showApiKeys)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2"
                      style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}
                    >
                      {showApiKeys ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                    </button>
                  </div>
                  {settings.openaiApiKey && (
                    <div className="mt-2 text-xs flex items-center justify-between">
                      <span style={getDescriptionStyle()}>
                        ✅ API 키가 설정되었습니다
                      </span>
                      <span style={getDescriptionStyle()}>
                        {settings.openaiApiKey.length}자
                      </span>
                    </div>
                  )}
                </div>

                {/* Anthropic */}
                <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="font-medium flex items-center" style={getLabelStyle()}>
                      🧠 Anthropic (Claude)
                    </h5>
                    <div className="text-xs" style={getDescriptionStyle()}>
                      높은 품질의 대화형 AI
                    </div>
                  </div>
                  <div className="relative">
                    <input
                      type={showApiKeys ? "text" : "password"}
                      value={settings.anthropicApiKey}
                      onChange={(e) => updateSetting('anthropicApiKey', e.target.value)}
                      style={getInputStyle()}
                      className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                      placeholder="sk-ant-..."
                    />
                  </div>
                  {settings.anthropicApiKey && (
                    <div className="mt-2 text-xs flex items-center justify-between">
                      <span style={getDescriptionStyle()}>
                        ✅ API 키가 설정되었습니다
                      </span>
                      <span style={getDescriptionStyle()}>
                        {settings.anthropicApiKey.length}자
                      </span>
                    </div>
                  )}
                </div>

                {/* Google */}
                <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="font-medium flex items-center" style={getLabelStyle()}>
                      🌟 Google (Gemini)
                    </h5>
                    <div className="text-xs" style={getDescriptionStyle()}>
                      구글의 최신 AI 모델
                    </div>
                  </div>
                  <div className="relative">
                    <input
                      type={showApiKeys ? "text" : "password"}
                      value={settings.googleApiKey}
                      onChange={(e) => updateSetting('googleApiKey', e.target.value)}
                      style={getInputStyle()}
                      className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                      placeholder="AIza..."
                    />
                  </div>
                  {settings.googleApiKey && (
                    <div className="mt-2 text-xs flex items-center justify-between">
                      <span style={getDescriptionStyle()}>
                        ✅ API 키가 설정되었습니다
                      </span>
                      <span style={getDescriptionStyle()}>
                        {settings.googleApiKey.length}자
                      </span>
                    </div>
                  )}
                </div>

                {/* Ollama - 단순화 */}
                <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800 md:col-span-2">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="font-medium flex items-center" style={getLabelStyle()}>
                      🦙 Ollama (로컬 AI)
                    </h5>
                    <span className="text-xs text-gray-500">로컬/원격 서버</span>
                  </div>
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={settings.ollamaServerUrl}
                      onChange={(e) => updateSetting('ollamaServerUrl', e.target.value)}
                      style={getInputStyle()}
                      className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                      placeholder="http://localhost:11434"
                    />
                    {settings.ollamaServerUrl && (() => {
                      const validation = validateOllamaUrl(settings.ollamaServerUrl);
                      return (
                        <div className={`text-xs px-2 py-1 rounded ${
                          validation.isValid 
                            ? 'text-green-600 bg-green-50 dark:bg-green-900/20' 
                            : 'text-red-600 bg-red-50 dark:bg-red-900/20'
                        }`}>
                          {validation.isValid ? '✅' : '❌'} {validation.message}
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* 기타 AI 제공자들 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* xAI */}
                  <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                    <h5 className="font-medium mb-2" style={getLabelStyle()}>
                      ⚡ xAI (Grok)
                    </h5>
                    <input
                      type={showApiKeys ? "text" : "password"}
                      value={settings.xaiApiKey}
                      onChange={(e) => updateSetting('xaiApiKey', e.target.value)}
                      style={getInputStyle()}
                      className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                      placeholder="xai-..."
                    />
                  </div>

                  {/* DeepSeek */}
                  <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                    <h5 className="font-medium mb-2" style={getLabelStyle()}>
                      🔍 DeepSeek
                    </h5>
                    <input
                      type={showApiKeys ? "text" : "password"}
                      value={settings.deepseekApiKey}
                      onChange={(e) => updateSetting('deepseekApiKey', e.target.value)}
                      style={getInputStyle()}
                      className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                      placeholder="sk-..."
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 용도별 AI 설정 */}
            <div className="p-6 rounded-lg border" style={getWidgetStyle()}>
              <h4 className="text-lg font-semibold mb-4 flex items-center" style={getLabelStyle()}>
                <DocumentChartBarIcon className="h-5 w-5 mr-2 text-orange-500" />
                용도별 AI 설정
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* 챗봇용 AI */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={getLabelStyle()}>
                    💬 챗봇 AI 제공자
                  </label>
                  <select
                    value={settings.chatbotAiProvider}
                    onChange={(e) => updateSetting('chatbotAiProvider', e.target.value)}
                    style={getInputStyle()}
                    className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="rule-based">규칙 기반</option>
                    <option value="openai">OpenAI (ChatGPT)</option>
                    <option value="anthropic">Anthropic (Claude)</option>
                    <option value="google">Google (Gemini)</option>
                    <option value="xai">xAI (Grok)</option>
                    <option value="deepseek">DeepSeek</option>
                    <option value="ollama">Ollama (로컬 AI)</option>
                  </select>
                  <p className="text-xs mt-1" style={getDescriptionStyle()}>
                    사용자와의 대화에 사용할 AI
                  </p>
                </div>

                {/* 분석용 AI */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={getLabelStyle()}>
                    📊 시장 분석 AI 제공자
                  </label>
                  <select
                    value={settings.analysisAiProvider}
                    onChange={(e) => updateSetting('analysisAiProvider', e.target.value)}
                    style={getInputStyle()}
                    className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="rule-based">규칙 기반</option>
                    <option value="openai">OpenAI (ChatGPT)</option>
                    <option value="anthropic">Anthropic (Claude)</option>
                    <option value="google">Google (Gemini)</option>
                    <option value="xai">xAI (Grok)</option>
                    <option value="deepseek">DeepSeek</option>
                    <option value="ollama">Ollama (로컬 AI)</option>
                  </select>
                  <p className="text-xs mt-1" style={getDescriptionStyle()}>
                    시장 분석에 사용할 AI
                  </p>
                </div>
              </div>
            </div>

            {/* AI 가이드 */}
            <div className="p-6 rounded-lg bg-gray-50 dark:bg-gray-800">
              <h4 className="text-lg font-semibold mb-4 flex items-center" style={getLabelStyle()}>
                📖 AI 설정 가이드
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm" style={getDescriptionStyle()}>
                <div>
                  <h5 className="font-medium mb-2" style={getLabelStyle()}>🚀 추천 설정</h5>
                  <ul className="space-y-1">
                    <li>• <strong>초보자:</strong> 단일 AI 모드 + OpenAI</li>
                    <li>• <strong>프라이버시 중시:</strong> Ollama 사용</li>
                    <li>• <strong>경제적:</strong> DeepSeek 또는 Ollama</li>
                    <li>• <strong>고성능:</strong> OpenAI 또는 Anthropic</li>
                  </ul>
                </div>
                
                <div>
                  <h5 className="font-medium mb-2" style={getLabelStyle()}>🔐 보안 팁</h5>
                  <ul className="space-y-1">
                    <li>• API 키는 안전하게 보관하세요</li>
                    <li>• 주기적으로 API 키를 교체하세요</li>
                    <li>• 사용량 제한을 설정하세요</li>
                    <li>• Ollama는 로컬 실행으로 가장 안전합니다</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );

      case 'notifications':
        return (
          <div className="space-y-8">
            {/* 알림 설정 헤더 */}
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold" style={getLabelStyle()}>🔔 알림 설정</h3>
              <div className="text-sm" style={getDescriptionStyle()}>
                텔레그램 및 브라우저 알림 관리
              </div>
            </div>

            {/* 텔레그램 알림 설정 */}
            <div className="p-6 rounded-lg border" style={getWidgetStyle()}>
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold flex items-center" style={getLabelStyle()}>
                  📱 텔레그램 알림
                </h4>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.telegramEnabled}
                    onChange={(e) => updateSetting('telegramEnabled', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 text-sm font-medium" style={getLabelStyle()}>
                    텔레그램 알림 활성화
                  </label>
                </div>
              </div>

              {settings.telegramEnabled && (
                <div className="space-y-6">
                  {/* 봇 토큰 설정 */}
                  <div>
                    <label className="block text-sm font-medium mb-2" style={getLabelStyle()}>
                      🤖 텔레그램 봇 토큰
                    </label>
                    <div className="relative">
                      <input
                        type={showApiKeys ? "text" : "password"}
                        value={settings.telegramBotToken}
                        onChange={(e) => updateSetting('telegramBotToken', e.target.value)}
                        style={getInputStyle()}
                        className="w-full border rounded-lg px-4 py-2 pr-12 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                        placeholder="123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11"
                      />
                      <button
                        type="button"
                        onClick={() => setShowApiKeys(!showApiKeys)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2"
                        style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}
                      >
                        {showApiKeys ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                      </button>
                    </div>
                    <p className="text-xs mt-1" style={getDescriptionStyle()}>
                      @BotFather에서 새 봇을 만들어 토큰을 받아주세요
                    </p>
                  </div>

                  {/* 채팅 ID 설정 */}
                  <div>
                    <label className="block text-sm font-medium mb-2" style={getLabelStyle()}>
                      👤 채팅 ID
                    </label>
                    <input
                      type="text"
                      value={settings.telegramChatId}
                      onChange={(e) => updateSetting('telegramChatId', e.target.value)}
                      style={getInputStyle()}
                      className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="123456789"
                    />
                    <p className="text-xs mt-1" style={getDescriptionStyle()}>
                      @userinfobot에게 /start를 보내면 채팅 ID를 확인할 수 있습니다
                    </p>
                  </div>

                  {/* 텔레그램 연결 테스트 */}
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={testTelegramConnection}
                      disabled={!settings.telegramBotToken || !settings.telegramChatId}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
                    >
                      📨 연결 테스트
                    </button>
                    <p className="text-xs" style={getDescriptionStyle()}>
                      설정이 올바른지 테스트 메시지를 보냅니다
                    </p>
                  </div>

                  {/* 알림 유형 설정 */}
                  <div>
                    <h5 className="text-sm font-medium mb-3" style={getLabelStyle()}>
                      📬 알림 유형 선택
                    </h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries({
                        tradeExecution: { label: '거래 실행', icon: '💰', desc: '매수/매도 시 알림' },
                        aiAnalysis: { label: 'AI 분석', icon: '🤖', desc: 'AI 시장 분석 결과' },
                        priceAlerts: { label: '가격 알림', icon: '📈', desc: '목표가 도달 시' },
                        profitLoss: { label: '손익 알림', icon: '💹', desc: '수익/손실 발생 시' },
                        errorMessages: { label: '오류 알림', icon: '⚠️', desc: '시스템 오류 발생 시' },
                        dailyReport: { label: '일일 리포트', icon: '📊', desc: '매일 성과 요약' }
                      }).map(([key, info]) => (
                        <div key={key} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-800">
                          <div className="flex items-center space-x-3">
                            <span>{info.icon}</span>
                            <div>
                              <div className="text-sm font-medium" style={getLabelStyle()}>
                                {info.label}
                              </div>
                              <div className="text-xs" style={getDescriptionStyle()}>
                                {info.desc}
                              </div>
                            </div>
                          </div>
                          <input
                            type="checkbox"
                            checked={settings.telegramNotifications[key as keyof typeof settings.telegramNotifications]}
                            onChange={(e) => updateSetting('telegramNotifications', {
                              ...settings.telegramNotifications,
                              [key]: e.target.checked
                            })}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 텔레그램 설정 가이드 */}
                  <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                    <h6 className="font-medium mb-2 text-blue-800 dark:text-blue-200">
                      📖 텔레그램 봇 설정 가이드
                    </h6>
                    <ol className="text-sm space-y-1 text-blue-700 dark:text-blue-300 list-decimal ml-4">
                      <li>텔레그램에서 @BotFather를 검색하여 대화 시작</li>
                      <li>/newbot 명령어로 새 봇 생성</li>
                      <li>봇 이름과 사용자명 설정 (예: ExTrader Bot, @extrader_bot)</li>
                      <li>받은 토큰을 위 입력칸에 복사</li>
                      <li>@userinfobot에게 /start를 보내 채팅 ID 확인</li>
                      <li>채팅 ID를 입력 후 연결 테스트</li>
                    </ol>
                  </div>
                </div>
              )}
            </div>

            {/* 브라우저 알림 설정 */}
            <div className="p-6 rounded-lg border" style={getWidgetStyle()}>
              <h4 className="text-lg font-semibold mb-4 flex items-center" style={getLabelStyle()}>
                🌐 브라우저 알림
              </h4>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                  <div className="flex items-center space-x-3">
                    <span>💰</span>
                    <div>
                      <div className="text-sm font-medium" style={getLabelStyle()}>거래 알림</div>
                      <div className="text-xs" style={getDescriptionStyle()}>매수/매도 시 브라우저 알림</div>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.tradeNotifications}
                    onChange={(e) => updateSetting('tradeNotifications', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                  <div className="flex items-center space-x-3">
                    <span>⚠️</span>
                    <div>
                      <div className="text-sm font-medium" style={getLabelStyle()}>오류 알림</div>
                      <div className="text-xs" style={getDescriptionStyle()}>시스템 오류 발생 시 알림</div>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.errorNotifications}
                    onChange={(e) => updateSetting('errorNotifications', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                  <div className="flex items-center space-x-3">
                    <span>📈</span>
                    <div>
                      <div className="text-sm font-medium" style={getLabelStyle()}>가격 알림</div>
                      <div className="text-xs" style={getDescriptionStyle()}>목표가 도달 시 알림</div>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.priceAlerts}
                    onChange={(e) => updateSetting('priceAlerts', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
              </div>

              {/* 브라우저 알림 권한 */}
              <div className="mt-6 p-4 rounded-lg bg-yellow-50 dark:bg-yellow-900/20">
                <h6 className="font-medium mb-2 text-yellow-800 dark:text-yellow-200">
                  🔔 브라우저 알림 권한
                </h6>
                <p className="text-sm mb-3 text-yellow-700 dark:text-yellow-300">
                  브라우저 알림을 받으려면 알림 권한을 허용해야 합니다.
                </p>
                <button
                  onClick={() => {
                    if ('Notification' in window) {
                      Notification.requestPermission().then(permission => {
                        if (permission === 'granted') {
                          showToast('알림 권한이 허용되었습니다.', 'success');
                        } else {
                          showToast('알림 권한이 거부되었습니다.', 'error');
                        }
                      });
                    } else {
                      showToast('이 브라우저는 알림을 지원하지 않습니다.', 'error');
                    }
                  }}
                  className="w-full font-medium py-2 px-4 rounded-lg transition-colors text-yellow-900 dark:text-yellow-100 bg-yellow-200 dark:bg-yellow-800 hover:bg-yellow-300 dark:hover:bg-yellow-700"
                >
                  🔔 알림 권한 요청
                </button>
              </div>
            </div>

            {/* 뉴스 수집 설정 */}
            <div className="p-6 rounded-lg border" style={getWidgetStyle()}>
              <h4 className="text-lg font-semibold mb-4 flex items-center" style={getLabelStyle()}>
                📰 뉴스 수집 설정
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2" style={getLabelStyle()}>
                    뉴스 업데이트 주기 (분)
                  </label>
                  <select
                    value={settings.newsUpdateInterval}
                    onChange={(e) => updateSetting('newsUpdateInterval', Number(e.target.value))}
                    style={getInputStyle()}
                    className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="5">5분</option>
                    <option value="10">10분</option>
                    <option value="15">15분 (권장)</option>
                    <option value="30">30분</option>
                    <option value="60">1시간</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={getLabelStyle()}>
                    최대 뉴스 개수
                  </label>
                  <input
                    type="number"
                    min="5"
                    max="50"
                    value={settings.newsMaxArticles}
                    onChange={(e) => updateSetting('newsMaxArticles', Number(e.target.value))}
                    style={getInputStyle()}
                    className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium" style={getLabelStyle()}>RSS 뉴스 수집</span>
                  <input
                    type="checkbox"
                    checked={settings.enableRssNews}
                    onChange={(e) => updateSetting('enableRssNews', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium" style={getLabelStyle()}>API 뉴스 수집</span>
                  <input
                    type="checkbox"
                    checked={settings.enableApiNews}
                    onChange={(e) => updateSetting('enableApiNews', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
              </div>
            </div>

            {/* 알림 테스트 */}
            <div className="p-6 rounded-lg border-2 border-green-300 dark:border-green-600 bg-green-50 dark:bg-green-900/20">
              <h4 className="text-lg font-semibold mb-4 flex items-center" style={getLabelStyle()}>
                🧪 알림 테스트
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={() => {
                    if (settings.telegramEnabled && settings.telegramBotToken && settings.telegramChatId) {
                      testTelegramConnection();
                    } else {
                      showToast('텔레그램 설정을 먼저 완료해주세요.', 'error');
                    }
                  }}
                  className="p-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                >
                  📱 텔레그램 테스트
                </button>
                
                <button
                  onClick={() => {
                    if ('Notification' in window && Notification.permission === 'granted') {
                      new Notification('ExTrader 알림 테스트', {
                        body: '브라우저 알림이 정상적으로 작동합니다!',
                        icon: '/favicon.ico'
                      });
                      showToast('브라우저 알림 테스트 완료', 'success');
                    } else {
                      showToast('브라우저 알림 권한을 먼저 허용해주세요.', 'error');
                    }
                  }}
                  className="p-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  🌐 브라우저 알림 테스트
                </button>
              </div>
            </div>
          </div>
        );

      case 'system':
        return (
          <div className="space-y-8">
            {/* 시스템 설정 헤더 */}
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold" style={getLabelStyle()}>⚙️ 시스템 설정</h3>
              <div className="text-sm" style={getDescriptionStyle()}>
                언어, 테마 및 시스템 관리
              </div>
            </div>

            {/* 기본 시스템 설정 */}
            <div className="p-6 rounded-lg border" style={getWidgetStyle()}>
              <h4 className="text-lg font-semibold mb-4 flex items-center" style={getLabelStyle()}>
                🎨 기본 설정
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2" style={getLabelStyle()}>
                    🌐 언어 설정
                  </label>
                  <select
                    value={settings.language}
                    onChange={(e) => updateSetting('language', e.target.value)}
                    style={getInputStyle()}
                    className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="ko">한국어</option>
                    <option value="en">English</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={getLabelStyle()}>
                    🎨 테마 설정
                  </label>
                  <select
                    value={settings.theme}
                    onChange={(e) => updateSetting('theme', e.target.value)}
                    style={getInputStyle()}
                    className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="dark">🌙 다크 테마</option>
                    <option value="light">☀️ 라이트 테마</option>
                  </select>
                </div>
              </div>
            </div>

            {/* 자동화 설정 */}
            <div className="p-6 rounded-lg border" style={getWidgetStyle()}>
              <h4 className="text-lg font-semibold mb-4 flex items-center" style={getLabelStyle()}>
                🤖 자동화 설정
              </h4>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                  <div className="flex items-center space-x-3">
                    <span>💾</span>
                    <div>
                      <div className="text-sm font-medium" style={getLabelStyle()}>자동 저장</div>
                      <div className="text-xs" style={getDescriptionStyle()}>설정 변경 시 자동으로 저장</div>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.autoSave}
                    onChange={(e) => updateSetting('autoSave', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                  <div className="flex items-center space-x-3">
                    <span>📦</span>
                    <div>
                      <div className="text-sm font-medium" style={getLabelStyle()}>자동 백업</div>
                      <div className="text-xs" style={getDescriptionStyle()}>정기적으로 설정 백업</div>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.autoBackup}
                    onChange={(e) => updateSetting('autoBackup', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>

                {settings.autoBackup && (
                  <div>
                    <label className="block text-sm font-medium mb-2" style={getLabelStyle()}>
                      백업 간격 (시간)
                    </label>
                    <input
                      type="number"
                      value={settings.backupInterval}
                      onChange={(e) => updateSetting('backupInterval', Number(e.target.value))}
                      style={getInputStyle()}
                      className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min="1"
                      max="168"
                    />
                    <p className="text-xs mt-1" style={getDescriptionStyle()}>설정 백업 주기 (1-168시간)</p>
                  </div>
                )}
              </div>
            </div>

            {/* 로깅 및 모니터링 */}
            <div className="p-6 rounded-lg border" style={getWidgetStyle()}>
              <h4 className="text-lg font-semibold mb-4 flex items-center" style={getLabelStyle()}>
                📊 로깅 및 모니터링
              </h4>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                  <div className="flex items-center space-x-3">
                    <span>📝</span>
                    <div>
                      <div className="text-sm font-medium" style={getLabelStyle()}>로깅 활성화</div>
                      <div className="text-xs" style={getDescriptionStyle()}>시스템 로그 기록</div>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.enableLogging}
                    onChange={(e) => updateSetting('enableLogging', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                  <div className="flex items-center space-x-3">
                    <span>⚡</span>
                    <div>
                      <div className="text-sm font-medium" style={getLabelStyle()}>성능 모니터링</div>
                      <div className="text-xs" style={getDescriptionStyle()}>시스템 성능 추적</div>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.enablePerformanceMonitoring}
                    onChange={(e) => updateSetting('enablePerformanceMonitoring', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>

                {settings.enableLogging && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2" style={getLabelStyle()}>
                        로그 레벨
                      </label>
                      <select
                        value={settings.logLevel}
                        onChange={(e) => updateSetting('logLevel', e.target.value)}
                        style={getInputStyle()}
                        className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="error">Error</option>
                        <option value="warn">Warning</option>
                        <option value="info">Info</option>
                        <option value="debug">Debug</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2" style={getLabelStyle()}>
                        로그 보관 기간 (일)
                      </label>
                      <input
                        type="number"
                        value={settings.logRetentionDays}
                        onChange={(e) => updateSetting('logRetentionDays', Number(e.target.value))}
                        style={getInputStyle()}
                        className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="1"
                        max="365"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 시스템 정보 */}
            <div className="p-6 rounded-lg bg-gray-50 dark:bg-gray-800">
              <h4 className="font-medium mb-2" style={getLabelStyle()}>📊 시스템 정보</h4>
              <div className="text-xs space-y-1" style={getDescriptionStyle()}>
                <p>• 버전: {versionInfo?.fullProductName || 'exTrader v1.8.9'}</p>
                <p>• 릴리즈 날짜: {versionInfo?.releaseDate || '2025-01-06'}</p>
                <p>• 빌드 번호: {versionInfo?.buildNumber || 'Unknown'}</p>
                <p>• 지원 거래소: {versionInfo?.exchanges?.join(', ') || '바이낸스, 업비트, 빗썸'}</p>
                <p>• AI 모델: {versionInfo?.aiProviders?.join(', ') || 'Rule-based, ChatGPT, Claude, Gemini, Ollama'} 지원</p>
                <p>• 주요 기능: {versionInfo?.features?.slice(0, 3).join(', ') || '텔레그램 알림, 실시간 뉴스, AI 분석'}</p>
                <p>• 마지막 업데이트: {versionInfo ? new Date().toLocaleDateString() : '확인 중...'}</p>
              </div>
            </div>

            {/* 데이터 소스 설정 */}
            <div className="p-6 rounded-lg border" style={getWidgetStyle()}>
              <h4 className="text-lg font-semibold mb-4 flex items-center" style={getLabelStyle()}>
                📰 외부 API 설정
              </h4>
              
              <div>
                <label className="block text-sm font-medium mb-2" style={getLabelStyle()}>
                  News API 키 (선택사항)
                </label>
                <div className="relative">
                  <input
                    type={showApiKeys ? "text" : "password"}
                    value={settings.newsApiKey}
                    onChange={(e) => updateSetting('newsApiKey', e.target.value)}
                    style={getInputStyle()}
                    className="w-full border rounded-lg px-4 py-2 pr-12 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                    placeholder="NewsAPI.org에서 발급받은 API 키"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKeys(!showApiKeys)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                    style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}
                  >
                    {showApiKeys ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </div>
                
                <div className="mt-2 text-xs space-y-1" style={getDescriptionStyle()}>
                  <p>• 뉴스 기반 시장 감성 분석을 위한 API 키입니다</p>
                  <p>• <a href="https://newsapi.org/" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-600">NewsAPI.org</a>에서 무료로 발급받을 수 있습니다</p>
                  <p>• 설정하지 않으면 기본 감성 지수가 사용됩니다</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'backtest':
        return (
          <div className="space-y-8">
            {/* 백테스트 설정 헤더 */}
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold" style={getLabelStyle()}>📈 백테스트 설정</h3>
              <div className="text-sm" style={getDescriptionStyle()}>
                과거 데이터를 활용한 전략 검증
              </div>
            </div>

            {/* 백테스트 기간 설정 */}
            <div className="p-6 rounded-lg border" style={getWidgetStyle()}>
              <h4 className="text-lg font-semibold mb-4 flex items-center" style={getLabelStyle()}>
                📅 백테스트 기간
              </h4>
              
              <div>
                <label className="block text-sm font-medium mb-2" style={getLabelStyle()}>
                  백테스트 기간 선택
                </label>
                <select
                  value={settings.backtestPeriod}
                  onChange={(e) => updateSetting('backtestPeriod', e.target.value)}
                  style={getInputStyle()}
                  className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="1month">1개월</option>
                  <option value="3months">3개월</option>
                  <option value="6months">6개월</option>
                  <option value="1year">1년 (권장)</option>
                  <option value="2years">2년</option>
                  <option value="custom">사용자 정의</option>
                </select>
                <p className="text-xs mt-1" style={getDescriptionStyle()}>
                  긴 기간일수록 더 신뢰할 수 있는 결과를 얻을 수 있습니다
                </p>
              </div>

              {settings.backtestPeriod === 'custom' && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={getLabelStyle()}>
                      시작 날짜
                    </label>
                    <input
                      type="date"
                      value={settings.backtestStartDate}
                      onChange={(e) => updateSetting('backtestStartDate', e.target.value)}
                      style={getInputStyle()}
                      className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={getLabelStyle()}>
                      종료 날짜
                    </label>
                    <input
                      type="date"
                      value={settings.backtestEndDate}
                      onChange={(e) => updateSetting('backtestEndDate', e.target.value)}
                      style={getInputStyle()}
                      className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* 백테스트 자본 설정 */}
            <div className="p-6 rounded-lg border" style={getWidgetStyle()}>
              <h4 className="text-lg font-semibold mb-4 flex items-center" style={getLabelStyle()}>
                💰 초기 자본 설정
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2" style={getLabelStyle()}>
                    초기 자본금 (USDT)
                  </label>
                  <input
                    type="number"
                    value={settings.backtestInitialCapital}
                    onChange={(e) => updateSetting('backtestInitialCapital', Number(e.target.value))}
                    style={getInputStyle()}
                    className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="1000"
                    max="1000000"
                    step="1000"
                  />
                  <p className="text-xs mt-1" style={getDescriptionStyle()}>
                    백테스트 시작 자본금 (최소 1,000 USDT)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={getLabelStyle()}>
                    거래 수수료 (%)
                  </label>
                  <input
                    type="number"
                    value={settings.backtestCommission}
                    onChange={(e) => updateSetting('backtestCommission', Number(e.target.value))}
                    style={getInputStyle()}
                    className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    max="1"
                    step="0.01"
                  />
                  <p className="text-xs mt-1" style={getDescriptionStyle()}>
                    거래당 수수료 (바이낸스 기본: 0.1%)
                  </p>
                </div>
              </div>
            </div>

            {/* 백테스트 실행 */}
            <div className="p-6 rounded-lg border-2 border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20">
              <h4 className="text-lg font-semibold mb-4 flex items-center" style={getLabelStyle()}>
                🚀 백테스트 실행
              </h4>
              
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-blue-100 dark:bg-blue-800/30">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    💡 백테스트는 과거 데이터를 기반으로 전략의 성과를 시뮬레이션합니다. 
                    실제 거래 성과와는 차이가 있을 수 있습니다.
                  </p>
                </div>
                
                <div className="text-center py-8" style={getDescriptionStyle()}>
                  <p className="text-lg mb-2">🔧 백테스트 기능 개발 중</p>
                  <p>현재 백테스트 엔진을 구축하고 있습니다.</p>
                  <p>완성되면 다양한 전략을 검증할 수 있습니다.</p>
                </div>
              </div>
            </div>

            {/* 백테스트 가이드 */}
            <div className="p-6 rounded-lg bg-gray-50 dark:bg-gray-800">
              <h4 className="text-lg font-semibold mb-4 flex items-center" style={getLabelStyle()}>
                📖 백테스트 가이드
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm" style={getDescriptionStyle()}>
                <div>
                  <h5 className="font-medium mb-2" style={getLabelStyle()}>📊 백테스트란?</h5>
                  <ul className="space-y-1">
                    <li>• 과거 데이터로 전략 성과 검증</li>
                    <li>• 리스크 대비 수익률 측정</li>
                    <li>• 최대 낙폭(MDD) 분석</li>
                    <li>• 샤프 비율 계산</li>
                  </ul>
                </div>
                
                <div>
                  <h5 className="font-medium mb-2" style={getLabelStyle()}>⚠️ 주의사항</h5>
                  <ul className="space-y-1">
                    <li>• 과거 성과는 미래를 보장하지 않음</li>
                    <li>• 슬리피지와 유동성 고려 필요</li>
                    <li>• 다양한 시장 상황 테스트 권장</li>
                    <li>• 실제 자금 투입 전 필수 검증</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );

       default:
         return (
           <div className="text-center py-8" style={getDescriptionStyle()}>
             설정 탭을 선택하세요
           </div>
         );
    }
  };

  // 테마에 따른 동적 스타일
  const getContainerStyle = () => ({
    backgroundColor: theme === 'dark' ? '#1a1f2e' : '#ffffff',
    color: theme === 'dark' ? '#ffffff' : '#1f2937'
  });

  const getWidgetStyle = () => ({
    backgroundColor: theme === 'dark' ? '#2d3748' : '#f8fafc',
    borderColor: theme === 'dark' ? '#4a5568' : '#e2e8f0'
  });

  const getInputStyle = () => ({
    backgroundColor: theme === 'dark' ? '#2d3748' : '#ffffff',
    borderColor: theme === 'dark' ? '#4a5568' : '#d1d5db',
    color: theme === 'dark' ? '#ffffff' : '#1f2937'
  });

  const getLabelStyle = () => ({
    color: theme === 'dark' ? '#ffffff' : '#374151'
  });

  const getDescriptionStyle = () => ({
    color: theme === 'dark' ? '#9ca3af' : '#6b7280'
  });

  // UI 레이아웃 편집 관련 함수들 추가
  const updateWidgetLayout = (widgetId: keyof typeof settings.uiLayout.dashboard, layout: { x: number; y: number; w: number; h: number; visible: boolean }) => {
    setSettings(prev => ({
      ...prev,
      uiLayout: {
        ...prev.uiLayout,
        dashboard: {
          ...prev.uiLayout.dashboard,
          [widgetId]: layout
        }
      }
    }));
  };

  const toggleWidgetVisibility = (widgetId: keyof typeof settings.uiLayout.dashboard) => {
    const widget = settings.uiLayout.dashboard[widgetId];
    updateWidgetLayout(widgetId, { ...widget, visible: !widget.visible });
  };

  const resetLayoutToDefault = () => {
    setSettings(prev => ({
      ...prev,
      uiLayout: {
        dashboard: {
          cryptoDashboard: { x: 0, y: 0, w: 12, h: 6, visible: true },
          botTimer: { x: 0, y: 6, w: 6, h: 3, visible: true },
          botControl: { x: 6, y: 6, w: 6, h: 3, visible: true },
          cryptoNews: { x: 0, y: 9, w: 12, h: 3, visible: true },
          performance: { x: 0, y: 12, w: 12, h: 3, visible: true },
          trades: { x: 0, y: 15, w: 12, h: 6, visible: true },
        },
        gridSize: 12,
        snapToGrid: true,
      }
    }));
    showToast('UI 레이아웃이 기본값으로 리셋되었습니다.', 'success');
  };

  const exportLayout = () => {
    const layoutData = JSON.stringify(settings.uiLayout, null, 2);
    const blob = new Blob([layoutData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ui-layout.json';
    a.click();
    URL.revokeObjectURL(url);
    showToast('레이아웃이 내보내기 되었습니다.', 'success');
  };

  const importLayout = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const layoutData = JSON.parse(e.target?.result as string);
        setSettings(prev => ({
          ...prev,
          uiLayout: layoutData
        }));
        showToast('레이아웃이 가져오기 되었습니다.', 'success');
      } catch (error) {
        showToast('잘못된 레이아웃 파일입니다.', 'error');
      }
    };
    reader.readAsText(file);
  };

  // 중앙화된 버전 관리 - 서버에서 가져오기
  useEffect(() => {
    fetch(`${getApiUrl()}/api/version`)
      .then(res => res.json())
      .then(data => setVersionInfo(data))
      .catch(err => console.error('버전 정보 로드 실패:', err));
  }, []);

  return (
    <div className="h-full flex flex-col" style={getContainerStyle()}>
      <div className="flex items-center mb-6">
        <Cog6ToothIcon className="h-6 w-6 mr-2 text-blue-500" />
        <h2 className="text-2xl font-bold" style={getLabelStyle()}>설정</h2>
        <div className="ml-auto text-xs" style={getDescriptionStyle()}>
          단계별 설정으로 거래 봇을 구성하세요
        </div>
      </div>
      
      {/* 탭 네비게이션 */}
      <div className="flex space-x-1 mb-6 p-1 rounded-lg overflow-x-auto border" style={getWidgetStyle()}>
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-blue-600 text-white'
                : theme === 'dark' 
                  ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <span className="mr-1 text-xs font-bold" style={getDescriptionStyle()}>{index + 1}</span>
            <tab.icon className="h-4 w-4 mr-2" />
            {tab.label}
          </button>
        ))}
      </div>
      
      {/* 탭 콘텐츠 */}
      <div className="flex-1 overflow-y-auto">
        <div className="rounded-lg p-6 border" style={getWidgetStyle()}>
          {renderTabContent()}
          
          {/* 저장 버튼 */}
          <div className="mt-8 pt-6 border-t" style={{ borderColor: theme === 'dark' ? '#4a5568' : '#e2e8f0' }}>
            <button
              onClick={saveSettings}
              disabled={saving}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? '저장 중...' : '설정 저장'}
            </button>
          </div>
        </div>
      </div>

      {/* 토스트 알림 */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={closeToast}
      />
    </div>
  );
};

export default SettingsComponent; 